import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Loader2, MapPin, MapPinOff, RotateCcw } from 'lucide-react';

import { checkInsApi } from '../../services/api';
import type { CheckInCampus, StaffCheckIn } from '../../types';
import {
    formatCenterTime,
    formatDistance,
    getCurrentPosition,
    rankCampusesByDistance,
    readLocationPermission,
    watchLocationPermission,
    type CampusDistance,
    type DevicePosition,
} from '../../utils/geolocation';
import { Button, Modal, Select, useToast } from '../ui';
import { classifyLocationProblem, detectBrowserPlatform, POOR_ACCURACY_M, type LocationProblem } from '../../utils/locationHelp';
import { LocationPermissionHelp, currentLocationGuide } from './LocationPermissionHelp';

// Câu ghi vào lần điểm danh để quản lý biết vì sao không có vị trí.
const PROBLEM_MESSAGES: Record<LocationProblem, string> = {
    'in-app': 'Mở trong Zalo/Facebook, không lấy được vị trí.',
    'site-blocked': 'Trang bị chặn quyền vị trí (đã bấm "Không cho phép").',
    'device-blocked': 'Điện thoại không cho trình duyệt dùng vị trí.',
    'blocked-unknown': 'Vị trí bị chặn (trên trang hoặc trong cài đặt máy).',
    'device-off': 'Máy chưa xác định được vị trí (định vị tắt hoặc tín hiệu yếu).',
    timeout: 'Hết thời gian chờ định vị.',
    unsupported: 'Trình duyệt không hỗ trợ định vị.',
};

type Step =
    | { kind: 'locating' }
    | { kind: 'located'; position: DevicePosition }
    | { kind: 'location-error'; message: string; problem?: LocationProblem }
    | { kind: 'submitting' }
    | { kind: 'done'; checkIn: StaffCheckIn }
    | { kind: 'registration-failed'; reason: string }
    | { kind: 'confirm-force'; reason: string };

export interface CheckInDialogProps {
    open: boolean;
    onClose: () => void;
    /**
     * arrival: record arrival at any campus (never blocked).
     * registration: re-check location for one campus before registering for a class there.
     */
    mode?: 'arrival' | 'registration';
    targetCampusId?: string;
    targetLabel?: string;
    onCheckedIn?: (checkIn: StaffCheckIn) => void;
    onForceRegister?: (reason: string) => void;
}

function statusText(checkIn: StaffCheckIn) {
    if (checkIn.status === 'IN_RANGE') return 'trong phạm vi cơ sở';
    if (checkIn.status === 'OUT_OF_RANGE') {
        return `ngoài phạm vi${checkIn.distanceM !== null ? ` (cách ≈${formatDistance(checkIn.distanceM)})` : ''}`;
    }
    return 'không xác định được vị trí';
}

export default function CheckInDialog({
    open,
    onClose,
    mode = 'arrival',
    targetCampusId,
    targetLabel,
    onCheckedIn,
    onForceRegister,
}: CheckInDialogProps) {
    const { showToast } = useToast();
    const [campuses, setCampuses] = useState<CheckInCampus[]>([]);
    const [step, setStep] = useState<Step>({ kind: 'locating' });
    const [selectedCampusId, setSelectedCampusId] = useState('');
    const pickedByUser = useRef(false);

    const locate = useCallback(async () => {
        setStep({ kind: 'locating' });
        try {
            const position = await getCurrentPosition();
            setStep({ kind: 'located', position });
        } catch (error: any) {
            const reason = error?.reason;
            if (!reason) {
                setStep({ kind: 'location-error', message: error?.message || 'Không lấy được vị trí.' });
                return;
            }
            // "Bị từ chối" có hai nguyên nhân; hỏi trạng thái quyền của trang để phân biệt.
            const permission = reason === 'denied' ? await readLocationPermission() : null;
            const problem = classifyLocationProblem(reason, permission, detectBrowserPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0));
            setStep({ kind: 'location-error', message: PROBLEM_MESSAGES[problem], problem });
        }
    }, []);

    // Khi đang bị chặn quyền: người dùng bật lại trong cài đặt rồi quay về thì tự định vị lại.
    const blocked = step.kind === 'location-error' && Boolean(step.problem) && step.problem !== 'unsupported' && step.problem !== 'in-app';
    useEffect(() => {
        if (!open || !blocked) return;
        const retryIfAllowed = async () => {
            const state = await readLocationPermission();
            if (state && state !== 'denied') locate();
        };
        const stopWatching = watchLocationPermission(state => { if (state !== 'denied') locate(); });
        const onVisible = () => { if (document.visibilityState === 'visible') retryIfAllowed(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', retryIfAllowed);
        return () => {
            stopWatching();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', retryIfAllowed);
        };
    }, [open, blocked, locate]);

    useEffect(() => {
        if (!open) return;
        pickedByUser.current = false;
        let active = true;
        checkInsApi.getToday()
            .then(response => {
                if (active) setCampuses(response.data.data.campuses);
            })
            .catch(() => {
                if (active) showToast('Không tải được danh sách cơ sở.', 'error');
            });
        locate();
        return () => {
            active = false;
        };
    }, [open, locate, showToast]);

    const ranked: CampusDistance[] = useMemo(
        () => (step.kind === 'located' ? rankCampusesByDistance(campuses, step.position) : []),
        [campuses, step],
    );
    const nearest = ranked[0];
    const target = mode === 'registration'
        ? ranked.find(item => item.campus.id === targetCampusId)
        : undefined;
    const targetCampus = campuses.find(campus => campus.id === targetCampusId);
    const selected = ranked.find(item => item.campus.id === selectedCampusId);

    // Default campus choice: the class campus when registering, else the nearest one (or the first).
    // Once the person picks a campus themselves, keep it.
    useEffect(() => {
        if (mode === 'registration' && targetCampusId) {
            setSelectedCampusId(targetCampusId);
        } else if (!pickedByUser.current) {
            setSelectedCampusId(nearest?.campus.id ?? campuses[0]?.id ?? '');
        }
    }, [mode, targetCampusId, nearest, campuses]);

    const submit = async (campusId: string, position: DevicePosition | null, locationError: string | null) => {
        setStep({ kind: 'submitting' });
        try {
            const response = await checkInsApi.create({
                campusId,
                latitude: position?.latitude ?? null,
                longitude: position?.longitude ?? null,
                accuracyM: position?.accuracyM ?? null,
                locationError,
            });
            const checkIn = response.data.data;
            if (mode === 'registration' && checkIn.status !== 'IN_RANGE') {
                setStep({ kind: 'registration-failed', reason: `Điểm danh ${statusText(checkIn)}` });
                return;
            }
            setStep({ kind: 'done', checkIn });
            onCheckedIn?.(checkIn);
        } catch (error: any) {
            showToast(error?.message || 'Điểm danh thất bại.', 'error');
            if (position) {
                setStep({ kind: 'located', position });
            } else {
                setStep({ kind: 'location-error', message: locationError || 'Không lấy được vị trí.' });
            }
        }
    };

    const campusSelect = (
        <Select
            label="Cơ sở"
            value={selectedCampusId}
            onChange={event => {
                pickedByUser.current = true;
                setSelectedCampusId(event.target.value);
            }}
            disabled={mode === 'registration'}
        >
            {campuses.map(campus => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
            ))}
        </Select>
    );

    // Có vị trí nhưng sai số rất lớn: thường do tắt "Vị trí chính xác" hoặc chỉ định vị qua mạng.
    const poorAccuracyHint = step.kind === 'located' && step.position.accuracyM > POOR_ACCURACY_M ? (
        <div className="space-y-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-warning-800" role="note">
            <p>
                Vị trí đang rất kém chính xác (sai số ≈{formatDistance(step.position.accuracyM)}), nên có thể bị tính là ngoài phạm vi.
                Hãy bật <strong>Vị trí chính xác</strong> cho trình duyệt (iPhone: Cài đặt → Quyền riêng tư & Bảo mật → Dịch vụ định vị → Safari/Chrome;
                Android: Cài đặt → Ứng dụng → Chrome → Quyền → Vị trí), bật Wi-Fi rồi thử lại.
            </p>
            <Button size="sm" variant="secondary" icon={<RotateCcw className="h-4 w-4" />} onClick={locate}>Định vị lại</Button>
        </div>
    ) : null;

    let body: React.ReactNode;
    let footer: React.ReactNode = null;

    if (step.kind === 'locating' || step.kind === 'submitting') {
        body = (
            <div className="flex items-center gap-3 py-4 text-sm text-ink-600">
                <Loader2 className="h-5 w-5 animate-spin text-academy-600" />
                {step.kind === 'locating' ? 'Đang xác định vị trí của bạn…' : 'Đang ghi nhận điểm danh…'}
            </div>
        );
    } else if (step.kind === 'done') {
        body = (
            <div className="flex items-start gap-3 rounded-lg bg-success-50 p-4 text-sm text-success-800">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <p>
                    Đã điểm danh tại <strong>{step.checkIn.campus.name}</strong> lúc{' '}
                    <strong>{formatCenterTime(step.checkIn.checkedInAt)}</strong> – {statusText(step.checkIn)}.
                </p>
            </div>
        );
        footer = <div className="flex justify-end"><Button onClick={onClose}>Đóng</Button></div>;
    } else if (step.kind === 'registration-failed') {
        body = (
            <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 rounded-lg bg-warning-50 p-4 text-warning-800">
                    <MapPinOff className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>{step.reason}. Bạn có thể thử lại, hoặc vẫn đăng ký lớp và quản lý sẽ nhận được thông báo kèm lý do.</p>
                </div>
            </div>
        );
        footer = (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="secondary" icon={<RotateCcw className="h-4 w-4" />} onClick={locate}>Thử lại</Button>
                <Button variant="danger" onClick={() => setStep({ kind: 'confirm-force', reason: step.reason })}>
                    Vẫn đăng ký lớp
                </Button>
            </div>
        );
    } else if (step.kind === 'confirm-force') {
        body = (
            <div className="space-y-2 text-sm text-ink-700">
                <p>Bạn chắc chắn vẫn đăng ký{targetLabel ? <> <strong>{targetLabel}</strong></> : ' lớp này'} khi chưa xác minh được vị trí?</p>
                <p className="rounded-lg bg-academy-50 p-3 text-ink-600">Lý do gửi quản lý: {step.reason}</p>
            </div>
        );
        footer = (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={() => setStep({ kind: 'registration-failed', reason: step.reason })}>
                    Quay lại
                </Button>
                <Button variant="danger" onClick={() => onForceRegister?.(step.reason)}>Xác nhận đăng ký</Button>
            </div>
        );
    } else if (step.kind === 'location-error') {
        body = (
            <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3 rounded-lg bg-warning-50 p-4 text-warning-800">
                    <MapPinOff className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>{step.message}</p>
                </div>
                {step.problem ? <LocationPermissionHelp problem={step.problem} /> : null}
                {mode === 'arrival' ? campusSelect : null}
            </div>
        );
        const needsReload = currentLocationGuide().needsReload
            && (step.problem === 'site-blocked' || step.problem === 'device-blocked' || step.problem === 'blocked-unknown');
        const retryButton = needsReload ? (
            <Button variant="secondary" icon={<RotateCcw className="h-4 w-4" />} onClick={() => window.location.reload()}>Tải lại trang</Button>
        ) : (
            <Button variant="secondary" icon={<RotateCcw className="h-4 w-4" />} onClick={locate}>Thử lại</Button>
        );
        footer = mode === 'registration' ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {retryButton}
                <Button variant="danger" onClick={() => setStep({ kind: 'confirm-force', reason: `Lỗi GPS: ${step.message}` })}>
                    Vẫn đăng ký lớp
                </Button>
            </div>
        ) : (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {retryButton}
                <Button
                    disabled={!selectedCampusId}
                    onClick={() => submit(selectedCampusId, null, step.message)}
                >
                    Điểm danh không có vị trí
                </Button>
            </div>
        );
    } else if (mode === 'registration') {
        const distanceText = target
            ? `Bạn đang cách ${target.campus.name} ≈${formatDistance(target.distanceM)}`
            : targetCampus
                ? `${targetCampus.name} chưa được cài đặt tọa độ`
                : 'Không tìm thấy cơ sở của lớp';
        body = (
            <div className="space-y-3 text-sm">
                <p className="text-ink-700">
                    Lần điểm danh tại cơ sở đã quá 10 phút. Vui lòng điểm danh lại để đăng ký
                    {targetLabel ? <> <strong>{targetLabel}</strong></> : ' lớp này'}.
                </p>
                <div className="flex items-start gap-3 rounded-lg bg-academy-50 p-4 text-ink-900">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>{distanceText}{target ? ` (sai số ±${step.position.accuracyM}m)` : ''}</p>
                </div>
                {poorAccuracyHint}
            </div>
        );
        footer = (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={onClose}>Huỷ</Button>
                <Button
                    disabled={!targetCampusId}
                    onClick={() => (target?.inRange
                        ? submit(targetCampusId!, step.position, null)
                        : setStep({ kind: 'registration-failed', reason: distanceText }))}
                >
                    Điểm danh &amp; đăng ký
                </Button>
            </div>
        );
    } else {
        const position = step.position;
        body = (
            <div className="space-y-4 text-sm">
                {nearest ? (
                    <div className={`flex items-start gap-3 rounded-lg p-4 ${nearest.inRange ? 'bg-academy-50 text-ink-900' : 'bg-warning-50 text-warning-800'}`}>
                        <MapPin className="mt-0.5 h-5 w-5 shrink-0" />
                        <p>
                            {nearest.inRange ? 'Bạn đang ở gần ' : 'Cơ sở gần nhất: '}
                            <strong>{nearest.campus.name}</strong> (≈{formatDistance(nearest.distanceM)}, sai số ±{position.accuracyM}m)
                            {nearest.inRange ? '' : '. Bạn vẫn có thể điểm danh, quản lý sẽ được thông báo.'}
                        </p>
                    </div>
                ) : (
                    <p className="rounded-lg bg-warning-50 p-4 text-warning-800">
                        Chưa có cơ sở nào được cài đặt tọa độ. Bạn vẫn có thể chọn cơ sở để điểm danh.
                    </p>
                )}
                {nearest && !nearest.inRange ? poorAccuracyHint : null}
                {campusSelect}
                {selected && selected.campus.id !== nearest?.campus.id ? (
                    <p className={selected.inRange ? 'text-academy-800' : 'text-warning-700'}>
                        Bạn đang cách {selected.campus.name} ≈{formatDistance(selected.distanceM)}
                        {selected.inRange ? '.' : ', ngoài phạm vi. Bạn vẫn có thể điểm danh, quản lý sẽ được thông báo.'}
                    </p>
                ) : null}
            </div>
        );
        footer = (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={onClose}>Để sau</Button>
                <Button disabled={!selectedCampusId} onClick={() => submit(selectedCampusId, position, null)}>
                    Điểm danh
                </Button>
            </div>
        );
    }

    if (!open) return null;

    return createPortal(
        <Modal
            open={open}
            onClose={onClose}
            title={mode === 'registration' ? 'Điểm danh lại để đăng ký' : 'Điểm danh tới cơ sở'}
            footer={footer}
            className="sm:max-w-md"
        >
            {body}
        </Modal>,
        document.body,
    );
}
